import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";

const ESTADOS = {
  aprobado:      { label: "Aprobado",      color: "#16a34a", bg: "#dcfce7" },
  condicional:   { label: "Condicional",   color: "#d97706", bg: "#fef3c7" },
  suspendido:    { label: "Suspendido",    color: "#dc2626", bg: "#fee2e2" },
  en_evaluacion: { label: "En evaluación", color: "#2A7FFF", bg: "#dbeafe" },
};

const CATEGORIAS = ["Materia prima","Insumos de impresión","Cartulina / Cartón","Tintas y barnices","Servicios de tercerización","Mantenimiento","Logística","Software","Otro"];
const NORMAS = ["ISO 9001:2015","ISO 14001:2015","FSC","FSSC 22000","No aplica"];
const CRITICIDAD = ["Alta","Media","Baja"];
const FRECUENCIA_EVAL = ["Mensual","Trimestral","Semestral","Anual"];

function hoy() { return new Date().toLocaleDateString('es-AR'); }
function generarCod(lista) { return `PRV-${String(lista.length + 1).padStart(4,"0")}`; }
function diasDesde(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}
function promedioEval(evaluaciones) {
  if (!evaluaciones?.length) return null;
  return (evaluaciones.reduce((a, e) => a + Number(e.puntaje), 0) / evaluaciones.length).toFixed(1);
}
function alertaReeval(prov) {
  if (!prov.ultimaEvaluacion || !prov.frecuenciaEval) return false;
  const dias = diasDesde(prov.ultimaEvaluacion);
  const limites = { Mensual: 30, Trimestral: 90, Semestral: 180, Anual: 365 };
  return dias > (limites[prov.frecuenciaEval] || 365);
}

const FORM_VACIO = {
  nombre:'', cuit:'', contacto:'', email:'', telefono:'', direccion:'',
  productoServicio:'', condicionesPago:'', plazoEntrega:'', observaciones:'',
  categoria:'Materia prima', norma:'ISO 9001:2015', estado:'en_evaluacion',
  criticidad:'Media', frecuenciaEval:'Anual',
};

export default function Proveedores({ onVolver, oscuro, usuario }) {
  const [proveedores, setProveedores] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [tabDetalle, setTabDetalle] = useState('datos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [showEvalForm, setShowEvalForm] = useState(false);
  const [evalForm, setEvalForm] = useState({ criterio:'', puntaje:'3', obs:'', fecha: new Date().toISOString().slice(0,10) });
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const esAdmin = usuario?.rol === 'Administrador';

  useEffect(() => {
    try { setProveedores(JSON.parse(localStorage.getItem('alqos_proveedores') || '[]')); } catch { setProveedores([]); }
  }, []);

  const guardarLS = (lista) => { setProveedores(lista); localStorage.setItem('alqos_proveedores', JSON.stringify(lista)); subirDatos('alqos_proveedores'); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardarProveedor = () => {
    if (!form.nombre.trim()) return alert('El nombre es obligatorio');
    if (editando) {
      const lista = proveedores.map(p => p.id === seleccionado.id ? { ...seleccionado, ...form } : p);
      guardarLS(lista);
      setSeleccionado({ ...seleccionado, ...form });
    } else {
      const nuevo = { ...form, id: Date.now(), codigo: generarCod(proveedores), evaluaciones: [], historialEstado: [], fechaCreacion: hoy(), creadoPor: usuario?.nombre || 'Sistema', ultimaEvaluacion: null };
      guardarLS([nuevo, ...proveedores]);
    }
    setShowForm(false); setEditando(false); setForm(FORM_VACIO);
  };

  const abrirEditar = (prov) => { setForm({ ...FORM_VACIO, ...prov }); setEditando(true); setShowForm(true); };

  const eliminar = async (id) => {
    setProveedores(proveedores.filter(p => p.id !== id));
    localStorage.setItem('alqos_proveedores', JSON.stringify(proveedores.filter(p => p.id !== id)));
    await eliminarDato('alqos_proveedores', id);
    setSeleccionado(null); setConfirmEliminar(null);
  };

  const cambiarEstado = (id, nuevoEstado) => {
    const lista = proveedores.map(p => {
      if (p.id !== id) return p;
      const entrada = { estado: nuevoEstado, fecha: hoy(), usuario: usuario?.nombre };
      return { ...p, estado: nuevoEstado, historialEstado: [...(p.historialEstado || []), entrada] };
    });
    guardarLS(lista);
    if (seleccionado?.id === id) setSeleccionado(lista.find(p => p.id === id));
  };

  const agregarEval = () => {
    if (!evalForm.criterio.trim()) return alert('Ingresá el criterio');
    const ev = { ...evalForm, id: Date.now(), registradoPor: usuario?.nombre, fechaRegistro: hoy() };
    const lista = proveedores.map(p => p.id === seleccionado.id
      ? { ...p, evaluaciones: [...(p.evaluaciones || []), ev], ultimaEvaluacion: new Date().toISOString() }
      : p);
    guardarLS(lista);
    setSeleccionado(lista.find(p => p.id === seleccionado.id));
    setEvalForm({ criterio:'', puntaje:'3', obs:'', fecha: new Date().toISOString().slice(0,10) });
    setShowEvalForm(false);
  };

  const ncsProv = (prov) => {
    try {
      const ncs = JSON.parse(localStorage.getItem('alqos_ncs') || '[]');
      return ncs.filter(nc => nc.proveedor === prov.nombre || nc.proveedor === prov.codigo);
    } catch { return []; }
  };

  const filtrados = proveedores.filter(p => {
    const matchEstado = filtroEstado === 'todos' || p.estado === filtroEstado;
    const matchBusq = !busqueda || p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || p.codigo?.toLowerCase().includes(busqueda.toLowerCase()) || p.productoServicio?.toLowerCase().includes(busqueda.toLowerCase());
    return matchEstado && matchBusq;
  });

  const inp = { width:'100%', padding:'8px 10px', borderRadius:'8px', border:'1px solid #E5E7EB', fontSize:'13px', boxSizing:'border-box', fontFamily:'Inter, sans-serif' };
  const lbl = { fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'4px', display:'block' };
  const secTit = { fontSize:'11px', fontWeight:'700', color:'#1C3FAA', margin:'14px 0 8px', paddingBottom:'5px', borderBottom:'1px solid #E8E9F6', textTransform:'uppercase', letterSpacing:'0.08em' };
  const pColor = (p) => Number(p) >= 4 ? '#16a34a' : Number(p) >= 3 ? '#d97706' : '#dc2626';

  const Campo = ({ k, v }) => v ? (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'6px', paddingBottom:'6px', borderBottom:'1px solid #F9FAFB' }}>
      <span style={{ color:'#6B7280' }}>{k}</span>
      <span style={{ color:'#111827', fontWeight:'500', textAlign:'right', maxWidth:'60%' }}>{v}</span>
    </div>
  ) : null;

  return (
    <div style={{ padding:'24px', fontFamily:'Inter, sans-serif', background:'#E8E9F6', minHeight:'100vh' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <button onClick={onVolver} style={{ background:'none', border:'1px solid #E5E7EB', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', color:'#6B7280' }}>← Menú</button>
          <BotonInstructivo moduloKey="proveedores" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="proveedores" oscuro={oscuro} />}
            <span style={{ fontSize:'22px' }}>🤝</span>
            <h1 style={{ fontSize:'20px', fontWeight:'700', color:'#1C3FAA', margin:0 }}>Proveedores</h1>
            <span style={{ fontSize:'11px', background:'#dbeafe', color:'#1C3FAA', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>ISO 9001 §8.4</span>
          </div>
          <p style={{ fontSize:'12px', color:'#6B7280', marginTop:'4px', marginLeft:'32px' }}>Control de proveedores externos · Evaluación y habilitación</p>
        </div>
        {esAdmin && <button onClick={() => { setForm(FORM_VACIO); setEditando(false); setShowForm(true); }}
          style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'10px', padding:'10px 18px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>+ Nuevo proveedor</button>}
      </div>

      {/* Métricas */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'10px', marginBottom:'20px' }}>
        {[
          { label:'Total', valor:proveedores.length, color:'#1C3FAA' },
          { label:'Aprobados', valor:proveedores.filter(p=>p.estado==='aprobado').length, color:'#16a34a' },
          { label:'Condicionales', valor:proveedores.filter(p=>p.estado==='condicional').length, color:'#d97706' },
          { label:'Suspendidos', valor:proveedores.filter(p=>p.estado==='suspendido').length, color:'#dc2626' },
          { label:'Reeval. vencida', valor:proveedores.filter(alertaReeval).length, color:'#9333ea' },
        ].map((m,i) => (
          <div key={i} style={{ background:'white', borderRadius:'12px', padding:'12px 14px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}>{m.label}</div>
            <div style={{ fontSize:'24px', fontWeight:'700', color:m.color }}>{m.valor}</div>
          </div>
        ))}
      </div>

      {/* Filtros y búsqueda */}
      <div style={{ display:'flex', gap:'10px', marginBottom:'14px', alignItems:'center', flexWrap:'wrap' }}>
        <input placeholder="🔍 Buscar por nombre, código o producto..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ ...inp, width:'280px', margin:0 }} />
        <div style={{ display:'flex', gap:'4px', background:'white', padding:'4px', borderRadius:'10px', border:'1px solid #E5E7EB' }}>
          {['todos',...Object.keys(ESTADOS)].map(k => (
            <button key={k} onClick={() => setFiltroEstado(k)}
              style={{ padding:'5px 12px', borderRadius:'7px', border:'none', fontSize:'11px', fontWeight:'500', cursor:'pointer', background:filtroEstado===k?'#1C3FAA':'transparent', color:filtroEstado===k?'white':'#6B7280' }}>
              {k==='todos'?'Todos':ESTADOS[k].label}
            </button>
          ))}
        </div>
      </div>

      {/* Layout */}
      <div style={{ display:'grid', gridTemplateColumns:seleccionado?'1fr 440px':'1fr', gap:'14px' }}>

        {/* Tabla */}
        <div style={{ background:'white', borderRadius:'14px', border:'1px solid #E5E7EB', overflow:'hidden' }}>
          {filtrados.length === 0 ? (
            <div style={{ padding:'60px', textAlign:'center', color:'#9CA3AF' }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>🤝</div>
              <div style={{ fontSize:'14px', fontWeight:'600' }}>Sin proveedores</div>
              <div style={{ fontSize:'12px', marginTop:'4px' }}>Registrá el primer proveedor con el botón de arriba</div>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Código / Nombre','Categoría','Criticidad','Producto / Servicio','Últ. evaluación','Puntaje','Estado',''].map(h => (
                    <th key={h} style={{ padding:'10px 14px', fontSize:'11px', fontWeight:'600', color:'#6B7280', textAlign:'left', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map(p => {
                  const est = ESTADOS[p.estado] || ESTADOS.en_evaluacion;
                  const prom = promedioEval(p.evaluaciones);
                  const reeval = alertaReeval(p);
                  return (
                    <tr key={p.id} onClick={() => { setSeleccionado(p); setTabDetalle('datos'); }}
                      style={{ cursor:'pointer', background:seleccionado?.id===p.id?'#EFF6FF':'white' }}
                      onMouseEnter={e => { if(seleccionado?.id!==p.id) e.currentTarget.style.background='#F9FAFB'; }}
                      onMouseLeave={e => { e.currentTarget.style.background=seleccionado?.id===p.id?'#EFF6FF':'white'; }}>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        <div style={{ fontWeight:'600', fontSize:'12px', color:'#1C3FAA' }}>{p.codigo}</div>
                        <div style={{ fontSize:'13px', fontWeight:'600', color:'#111827' }}>{p.nombre || '—'}</div>
                      </td>
                      <td style={{ padding:'11px 14px', fontSize:'12px', color:'#374151', borderBottom:'1px solid #F3F4F6' }}>{p.categoria}</td>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        <span style={{ fontSize:'11px', fontWeight:'600', padding:'2px 8px', borderRadius:'6px',
                          background:p.criticidad==='Alta'?'#fee2e2':p.criticidad==='Media'?'#fef3c7':'#f0fdf4',
                          color:p.criticidad==='Alta'?'#dc2626':p.criticidad==='Media'?'#d97706':'#16a34a' }}>
                          {p.criticidad||'—'}
                        </span>
                      </td>
                      <td style={{ padding:'11px 14px', fontSize:'12px', color:'#374151', borderBottom:'1px solid #F3F4F6', maxWidth:'160px' }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.productoServicio||'—'}</div>
                      </td>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        {reeval
                          ? <span style={{ fontSize:'11px', background:'#fee2e2', color:'#dc2626', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>⚠ Vencida</span>
                          : <span style={{ fontSize:'11px', color:'#6B7280' }}>{p.ultimaEvaluacion ? new Date(p.ultimaEvaluacion).toLocaleDateString('es-AR') : '—'}</span>}
                      </td>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        {prom ? <span style={{ fontSize:'13px', fontWeight:'700', color:pColor(prom) }}>{prom}/5</span> : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        <span style={{ background:est.bg, color:est.color, fontSize:'11px', padding:'3px 9px', borderRadius:'20px', fontWeight:'600' }}>{est.label}</span>
                      </td>
                      <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                        {reeval && <span title="Reevaluación vencida" style={{ fontSize:'16px' }}>🔴</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Panel detalle */}
        {seleccionado && (
          <div style={{ background:'white', borderRadius:'14px', border:'1px solid #E5E7EB', overflow:'hidden', maxHeight:'82vh', overflowY:'auto' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:'12px', color:'#1C3FAA', fontWeight:'600' }}>{seleccionado.codigo}</div>
                <div style={{ fontWeight:'700', fontSize:'15px', color:'#111827' }}>{seleccionado.nombre}</div>
                <div style={{ marginTop:'6px', display:'flex', gap:'5px', flexWrap:'wrap' }}>
                  <span style={{ background:ESTADOS[seleccionado.estado]?.bg, color:ESTADOS[seleccionado.estado]?.color, fontSize:'11px', padding:'2px 8px', borderRadius:'20px', fontWeight:'600' }}>{ESTADOS[seleccionado.estado]?.label}</span>
                  <span style={{ background:'#E8E9F6', color:'#1C3FAA', fontSize:'11px', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>{seleccionado.criticidad}</span>
                  {alertaReeval(seleccionado) && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:'11px', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>⚠ Reevaluar</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:'6px' }}>
                {esAdmin && <button onClick={() => abrirEditar(seleccionado)} style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'8px', padding:'6px 10px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>✏️ Editar</button>}
                {esAdmin && <button onClick={() => setConfirmEliminar(seleccionado.id)} style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:'8px', padding:'6px 10px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>🗑</button>}
                <button onClick={() => setSeleccionado(null)} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#9CA3AF' }}>✕</button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', gap:'2px', padding:'8px 12px', borderBottom:'1px solid #F3F4F6', background:'#F9FAFB' }}>
              {[['datos','📋 Datos'],['evaluaciones','⭐ Evaluaciones'],['estado','🔄 Estado'],['ncs','⚠️ NCs']].map(([id,lb]) => (
                <button key={id} onClick={() => setTabDetalle(id)}
                  style={{ padding:'5px 12px', borderRadius:'7px', border:'none', fontSize:'11px', fontWeight:'500', cursor:'pointer', background:tabDetalle===id?'#1C3FAA':'transparent', color:tabDetalle===id?'white':'#6B7280' }}>{lb}</button>
              ))}
            </div>

            <div style={{ padding:'14px 16px' }}>

              {tabDetalle === 'datos' && (<>
                <div style={secTit}>Datos generales</div>
                <Campo k="Categoría" v={seleccionado.categoria} />
                <Campo k="Norma aplicable" v={seleccionado.norma} />
                <Campo k="Criticidad" v={seleccionado.criticidad} />
                <Campo k="Frecuencia de reevaluación" v={seleccionado.frecuenciaEval} />
                <Campo k="CUIT" v={seleccionado.cuit} />
                <Campo k="Contacto" v={seleccionado.contacto} />
                <Campo k="Email" v={seleccionado.email} />
                <Campo k="Teléfono" v={seleccionado.telefono} />
                <Campo k="Dirección" v={seleccionado.direccion} />
                <div style={secTit}>Condiciones comerciales</div>
                <Campo k="Producto / Servicio" v={seleccionado.productoServicio} />
                <Campo k="Condiciones de pago" v={seleccionado.condicionesPago} />
                <Campo k="Plazo de entrega" v={seleccionado.plazoEntrega} />
                <Campo k="Creado por" v={seleccionado.creadoPor} />
                <Campo k="Fecha creación" v={seleccionado.fechaCreacion} />
                {seleccionado.observaciones && (
                  <div style={{ marginTop:'10px', background:'#F9FAFB', borderRadius:'8px', padding:'10px', fontSize:'12px', color:'#374151' }}>
                    <div style={{ fontWeight:'600', fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}>Observaciones</div>
                    {seleccionado.observaciones}
                  </div>
                )}
              </>)}

              {tabDetalle === 'evaluaciones' && (<>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:'600', color:'#374151' }}>
                      {seleccionado.evaluaciones?.length || 0} evaluaciones
                      {promedioEval(seleccionado.evaluaciones) && <span style={{ marginLeft:'8px', color:pColor(promedioEval(seleccionado.evaluaciones)), fontWeight:'700' }}>· Promedio: {promedioEval(seleccionado.evaluaciones)}/5</span>}
                    </div>
                    <div style={{ fontSize:'11px', color:'#6B7280' }}>Frecuencia: {seleccionado.frecuenciaEval || '—'}</div>
                  </div>
                  {esAdmin && <button onClick={() => setShowEvalForm(e => !e)}
                    style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>+ Agregar</button>}
                </div>

                {showEvalForm && (
                  <div style={{ background:'#F9FAFB', borderRadius:'10px', padding:'12px', marginBottom:'12px', border:'1px solid #E5E7EB' }}>
                    <label style={lbl}>Criterio evaluado</label>
                    <input style={inp} placeholder="Ej: Calidad de entrega, Cumplimiento de plazos..." value={evalForm.criterio} onChange={e => setEvalForm(f => ({ ...f, criterio:e.target.value }))} />
                    <label style={{ ...lbl, marginTop:'10px' }}>Puntaje (1 al 5)</label>
                    <div style={{ display:'flex', gap:'6px', marginTop:'4px' }}>
                      {['1','2','3','4','5'].map(p => (
                        <button key={p} onClick={() => setEvalForm(f => ({ ...f, puntaje:p }))}
                          style={{ flex:1, padding:'8px', borderRadius:'8px', fontSize:'14px', cursor:'pointer', fontWeight:'700', border:evalForm.puntaje===p?`2px solid ${pColor(p)}`:'1px solid #E5E7EB', background:evalForm.puntaje===p?`${pColor(p)}22`:'white', color:evalForm.puntaje===p?pColor(p):'#6B7280' }}>{p}</button>
                      ))}
                    </div>
                    <label style={{ ...lbl, marginTop:'10px' }}>Fecha</label>
                    <input type="date" style={inp} value={evalForm.fecha} onChange={e => setEvalForm(f => ({ ...f, fecha:e.target.value }))} />
                    <label style={{ ...lbl, marginTop:'10px' }}>Observaciones</label>
                    <textarea style={{ ...inp, minHeight:'50px', resize:'vertical' }} value={evalForm.obs} onChange={e => setEvalForm(f => ({ ...f, obs:e.target.value }))} />
                    <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
                      <button onClick={agregarEval} style={{ flex:1, padding:'8px', background:'#1C3FAA', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>Guardar</button>
                      <button onClick={() => setShowEvalForm(false)} style={{ padding:'8px 14px', background:'none', border:'1px solid #E5E7EB', borderRadius:'8px', cursor:'pointer', color:'#6B7280', fontSize:'12px' }}>Cancelar</button>
                    </div>
                  </div>
                )}

                {(seleccionado.evaluaciones || []).length === 0
                  ? <div style={{ textAlign:'center', padding:'30px', color:'#9CA3AF', fontSize:'13px' }}>Sin evaluaciones registradas</div>
                  : [...(seleccionado.evaluaciones || [])].reverse().map(ev => (
                    <div key={ev.id} style={{ background:'#F9FAFB', borderRadius:'10px', padding:'10px 12px', marginBottom:'8px', border:'1px solid #E5E7EB' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                        <span style={{ fontSize:'12px', fontWeight:'600', color:'#374151' }}>{ev.criterio}</span>
                        <span style={{ fontSize:'13px', fontWeight:'700', color:pColor(ev.puntaje) }}>{ev.puntaje}/5</span>
                      </div>
                      {ev.obs && <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'3px' }}>{ev.obs}</div>}
                      <div style={{ fontSize:'11px', color:'#9CA3AF' }}>{ev.fecha} · {ev.registradoPor}</div>
                    </div>
                  ))
                }
              </>)}

              {tabDetalle === 'estado' && (<>
                <div style={secTit}>Cambiar estado de habilitación</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
                  {Object.entries(ESTADOS).map(([val, { label, color, bg }]) => (
                    <button key={val} onClick={() => esAdmin && cambiarEstado(seleccionado.id, val)}
                      style={{ padding:'10px', borderRadius:'10px', border:`1.5px solid ${seleccionado.estado===val?color:'#E5E7EB'}`, background:seleccionado.estado===val?bg:'white', color:seleccionado.estado===val?color:'#6B7280', fontSize:'12px', fontWeight:'600', cursor:esAdmin?'pointer':'default', textAlign:'center' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={secTit}>Historial de estados</div>
                {(seleccionado.historialEstado || []).length === 0
                  ? <div style={{ fontSize:'12px', color:'#9CA3AF', textAlign:'center', padding:'20px' }}>Sin cambios registrados</div>
                  : [...(seleccionado.historialEstado || [])].reverse().map((h, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                      <span style={{ background:ESTADOS[h.estado]?.bg, color:ESTADOS[h.estado]?.color, padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:'600' }}>{ESTADOS[h.estado]?.label}</span>
                      <span style={{ color:'#6B7280' }}>{h.fecha} · {h.usuario}</span>
                    </div>
                  ))
                }
              </>)}

              {tabDetalle === 'ncs' && (<>
                <div style={secTit}>No Conformidades vinculadas</div>
                {ncsProv(seleccionado).length === 0
                  ? <div style={{ textAlign:'center', padding:'30px', color:'#9CA3AF', fontSize:'13px' }}>Sin NCs vinculadas a este proveedor</div>
                  : ncsProv(seleccionado).map(nc => (
                    <div key={nc.id} style={{ background:'#FFF5F5', border:'1px solid #FECACA', borderRadius:'10px', padding:'10px 12px', marginBottom:'8px' }}>
                      <div style={{ fontSize:'12px', fontWeight:'600', color:'#DC2626' }}>{nc.codigo || `NC-${nc.id}`}</div>
                      <div style={{ fontSize:'12px', color:'#374151', marginTop:'2px' }}>{nc.descripcion}</div>
                      <div style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'3px' }}>{nc.fecha}</div>
                    </div>
                  ))
                }
              </>)}
            </div>
          </div>
        )}
      </div>

      {/* Modal form */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', width:'560px', maxHeight:'92vh', overflowY:'auto', padding:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h2 style={{ fontSize:'16px', fontWeight:'700', color:'#1C3FAA', margin:0 }}>{editando ? '✏️ Editar proveedor' : '🤝 Nuevo proveedor'}</h2>
              <button onClick={() => { setShowForm(false); setEditando(false); }} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#9CA3AF' }}>✕</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Razón social / Nombre *</label><input style={inp} placeholder="Ej: Papelera del Sur SA" value={form.nombre} onChange={e => set('nombre', e.target.value)} /></div>
              <div><label style={lbl}>Categoría</label>
                <select style={inp} value={form.categoria} onChange={e => set('categoria', e.target.value)}>{CATEGORIAS.map(c => <option key={c}>{c}</option>)}</select>
              </div>
              <div><label style={lbl}>Norma aplicable</label>
                <select style={inp} value={form.norma} onChange={e => set('norma', e.target.value)}>{NORMAS.map(n => <option key={n}>{n}</option>)}</select>
              </div>
              <div><label style={lbl}>Criticidad</label>
                <select style={inp} value={form.criticidad} onChange={e => set('criticidad', e.target.value)}>{CRITICIDAD.map(c => <option key={c}>{c}</option>)}</select>
              </div>
              <div><label style={lbl}>Frecuencia reevaluación</label>
                <select style={inp} value={form.frecuenciaEval} onChange={e => set('frecuenciaEval', e.target.value)}>{FRECUENCIA_EVAL.map(f => <option key={f}>{f}</option>)}</select>
              </div>
              <div><label style={lbl}>CUIT</label><input style={inp} placeholder="20-12345678-9" value={form.cuit} onChange={e => set('cuit', e.target.value)} /></div>
              <div><label style={lbl}>Contacto</label><input style={inp} placeholder="Nombre del contacto" value={form.contacto} onChange={e => set('contacto', e.target.value)} /></div>
              <div><label style={lbl}>Email</label><input style={inp} placeholder="contacto@empresa.com" value={form.email} onChange={e => set('email', e.target.value)} /></div>
              <div><label style={lbl}>Teléfono</label><input style={inp} placeholder="11-1234-5678" value={form.telefono} onChange={e => set('telefono', e.target.value)} /></div>
              <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Dirección</label><input style={inp} placeholder="Calle, número, ciudad" value={form.direccion} onChange={e => set('direccion', e.target.value)} /></div>
              <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Producto / Servicio que provee</label><input style={inp} placeholder="Ej: Cartulina SBS, Tintas UV..." value={form.productoServicio} onChange={e => set('productoServicio', e.target.value)} /></div>
              <div><label style={lbl}>Condiciones de pago</label><input style={inp} placeholder="Ej: 30 días" value={form.condicionesPago} onChange={e => set('condicionesPago', e.target.value)} /></div>
              <div><label style={lbl}>Plazo de entrega</label><input style={inp} placeholder="Ej: 5 días hábiles" value={form.plazoEntrega} onChange={e => set('plazoEntrega', e.target.value)} /></div>
              <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Estado inicial</label>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'4px' }}>
                  {Object.entries(ESTADOS).map(([val,{label,color,bg}]) => (
                    <button key={val} onClick={() => set('estado', val)}
                      style={{ padding:'6px 12px', borderRadius:'8px', border:`1.5px solid ${form.estado===val?color:'#E5E7EB'}`, background:form.estado===val?bg:'white', color:form.estado===val?color:'#6B7280', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>{label}</button>
                  ))}
                </div>
              </div>
              <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Observaciones</label>
                <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} placeholder="Notas, condiciones especiales..." value={form.observaciones} onChange={e => set('observaciones', e.target.value)} />
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
              <button onClick={() => { setShowForm(false); setEditando(false); }} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={guardarProveedor} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Guardar proveedor</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm eliminar */}
      {confirmEliminar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'28px', maxWidth:'340px', width:'90%', textAlign:'center' }}>
            <div style={{ fontSize:'36px', marginBottom:'12px' }}>🗑</div>
            <div style={{ fontSize:'15px', fontWeight:'600', color:'#111827', marginBottom:'8px' }}>¿Eliminar este proveedor?</div>
            <div style={{ fontSize:'12px', color:'#6B7280', marginBottom:'20px' }}>Se eliminará también su historial de evaluaciones y estados.</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => setConfirmEliminar(null)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={() => eliminar(confirmEliminar)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'none', background:'#dc2626', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
